import { InputFile } from '@components/form/input';
import { HrCustomButton, HrFlexContainer, HrLabel, HrParagraph, HrIcon, Spinner, HrRed } from '@components/styles/hr-styles';
import React, { useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { serialize } from 'object-to-formdata';
import { ErrorMessage } from '@hookform/error-message';
import { PropTypes } from 'prop-types';
import ReactHintFactory from 'react-hint';
import styled from 'styled-components';
import Toast from '../../pipeline/components/Toast';

const ReactHint = ReactHintFactory(React);

const ReactHintooltip = styled(ReactHint)`
  font-family: 'Open Sans', sans-serif;
  font-size: 14px;
`;

const ErrorText = styled(HrParagraph)`
  margin: 2px 0 0 0;
  font-size: 13px;
  line-height: 1.3;
`;

export const MultipleFile = ({
  limitFiles,
  sizeFiles,
  accept,
  control,
  register,
  setValue,
  unregister,
  getValues,
  trigger,
  setIsUploadingFile,
  existingFiles,
  onOpenExistingFile,
  fieldError,
  clearFieldError,
  required
}) => {
  const { setError, clearErrors, formState } = useForm({});
  const { errors } = formState;
  const inputRef = useRef();
  const abortController = {};
  const isInitializedRef = useRef(false);
  const [fileSelected, setFileSelected] = useState({ name: '' });
  const [files, setFiles] = useState([]);
  const [disabledButtonFile, setdisabledButtonFile] = useState(false);
  const [toastState, setToastState] = useState({
    visible: false,
    message: null,
    typeToast: null
  });
  const [t] = useTranslation();

  useEffect(() => {
    register('attachedFiles');
  }, []);

  useEffect(() => {
    if (isInitializedRef.current) return;
    if (!Array.isArray(existingFiles) || existingFiles.length === 0) return;

    isInitializedRef.current = true;
    setFiles(
      existingFiles.map((file) => ({
        name: file.fileName || file.originalFileName || 'Archivo adjunto',
        status: 'uploaded',
        existing: true,
        uri: file.uri || null
      }))
    );
    setValue('attachedFiles', existingFiles);
  }, [existingFiles, setValue]);

  useEffect(() => {
    const addFileToState = async () => {
      const fileExists = files.some((file) => file.name === fileSelected.name);
      if (fileExists) {
        setToastState({ visible: true, type: 'error', message: t(`postulant.error.loadedFile`) });
      } else if (fileSelected.name) {
        clearFieldError?.();
        const isFileValid = await trigger('cv_attachedFiles');

        const newFile = {
          name: fileSelected.name,
          status: isFileValid ? 'added' : 'invalid',
          file: fileSelected.file,
          source: fileSelected.source,
          currentError: isFileValid !== true
        };

        const updatedFiles = [...files, newFile];
        setFiles(updatedFiles);
      }
    };

    const cleanup = () => {
      setValue('cv_attachedFiles', null);
    };

    addFileToState();
    return cleanup;
  }, [fileSelected]);
  //   Guardamos los archivos
  const saveRequestFile = (file, name, source) => {
    setError(`loadingfiles__${name}`, 'error');
    // eslint-disable-next-line no-undef
    abortController[name] = new AbortController();

    const promise = window
      .fetch('/api/requisitions/saveTemporalFile', {
        method: 'POST',
        body: serialize({ cv_file: file }),
        mode: 'cors',
        signal: abortController[name].signal
      })
      .then((res) => res.json())
      .then((res) => {
        const { response, result } = res;
        const { temporalFileName } = response.result;

        const findFile = files.find((_file) => _file.name === name);
        if (findFile && findFile.status === 'loading') {
          setFiles((prev) => {
            const _prev = [...prev] || [];
            const index = _prev.findIndex((_file) => _file.name === name);
            _prev[index].status = result;
            return _prev;
          });
          const currentAttachedFiles = getValues('attachedFiles') || [];
          setValue('attachedFiles', [
            ...currentAttachedFiles,
            {
              temporalFileName,
              originalFileName: name
            }
          ]);

          setValue('cv_attachedFiles', null);
          clearErrors(`loadingfiles__${name}`);
          setIsUploadingFile(false);
        }
      })
      .catch((err) => {
        console.error(`Download error: ${err.message}`);
        setToastState({ visible: true, type: 'error', message: t(`postulant.error.invalidFileForm`) });
      });
  };

  useEffect(() => {
    const _files = [...files];
    if (_files.length === limitFiles) {
      setdisabledButtonFile(true);
    } else {
      setdisabledButtonFile(false);
    }
    if (_files.length === 0) {
      unregister('attachedFiles', { keepValue: true });
    }
    const fileSave = _files?.find((file) => file.status === 'added');
    if (fileSave) {
      const { file, name, source } = fileSave;
      const index = _files.indexOf(fileSave);
      setIsUploadingFile(true);
      _files[index].status = 'loading';
      setFiles(_files);
      saveRequestFile(file, name, source);
    }
  }, [files]);

  const handleDeleteFile = (name) => {
    const _files = [...files];
    const fileToDelete = _files.find((_file) => _file.name === name);
    const filesUp = _files.filter((_file) => _file.name !== name);
    const formFiles = [...(getValues('attachedFiles') || [])];
    const formFilesUp = formFiles.filter((file) => {
      if (fileToDelete?.uri) {
        return file.uri !== fileToDelete.uri;
      }
      const originalName = file.fileName || file.originalFileName || file.name;
      return originalName !== name;
    });
    clearErrors(`loadingfiles__${name}`);
    clearErrors(`cv_attachedFiles`);
    setValue('cv_attachedFiles', null);
    setValue('attachedFiles', formFilesUp);
    setFiles(filesUp);
    inputRef.current.value = null;
    if (abortController[name]) {
      abortController[name].abort();
    }
  };

  const renderFilesSelected = () => {
    if (getValues('attachedFiles')?.length === 0) {
      return null;
    }
    return (
      <HrFlexContainer flexDirection='column' alignItems='flex-start'>
        {files.map((file) => {
          return (
            <HrFlexContainer
              key={file.name}
              padding='8px'
              borderRadius='8px'
              backgroundColor='#f4f4f4'
              width='fit-content'
              borderColor={file.status === 'error' ? '#ED6B75' : '#F4F4F4'}
              borderWidth={file.status === 'error' && '1px'}
            >
              <HrParagraph>{file.name}</HrParagraph>
              {file.status === 'invalid' && (
                <>
                  <HrParagraph color={HrRed}>
                    <HrIcon className='hr-No-qualified' color={HrRed} />
                    <span>{t('postulant.error.invalidFileForm')}</span>
                  </HrParagraph>
                </>
              )}
              {file.status === 'error' && (
                <HrParagraph color={HrRed}>
                  <HrIcon className='hr-No-qualified' color={HrRed} />{' '}
                  <span>{t('postulant.error.invalidFileForm')}</span>
                </HrParagraph>
              )}

              {file.status === 'loading' ? (
                <Spinner className='hr-Charge' />
              ) : (
                <>
                  {file.uri && (
                    <HrIcon
                      className='hr-Eye-open'
                      color='#24bb72'
                      onClick={() => {
                        onOpenExistingFile?.(file);
                      }}
                      style={{ cursor: 'pointer' }}
                    />
                  )}
                  <HrIcon
                    className='hr-Trash'
                    color='#ED6B75'
                    onClick={() => {
                      handleDeleteFile(file.name);
                    }}
                    style={{ cursor: 'pointer' }}
                  />
                </>
              )}
            </HrFlexContainer>
          );
        })}
      </HrFlexContainer>
    );
  };
  return (
    <HrFlexContainer flexDirection='column' alignItems='flex-start' gap='6px' width='100%'>
      <HrLabel isRequired={required} style={{ fontWeight: '600', fontSize: '14px' }}>
        Archivo adjunto
      </HrLabel>
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
        <HrCustomButton
          type='button'
          padding='16px'
          borderWidth='1px'
          fontSize='14px'
          minHeight='40px'
          height='40px'
          maxHeight='40px'
          color={disabledButtonFile ? '#A7E4C7' : '#24bb72'}
          gap='0px'
          backgroundColor='#fff'
          borderColor={disabledButtonFile ? '#A7E4C7' : '#24bb72'}
          borderColorHover={disabledButtonFile ? '#A7E4C7' : '#24BB72'}
          borderColorActive={!disabledButtonFile && '#24bb72'}
          backgroundColorHover={!disabledButtonFile && '#F0FFF8'}
          className='dropbox-button'
          backgroundColorDisabled='#fff'
          data-rh='Se ha alcanzado el límite de 3 archivos'
          data-rh-at='bottom'
          disabled={disabledButtonFile}
          onClick={() => {
            inputRef.current.click();
          }}
        >
          <HrIcon className='hr-Attach' color={disabledButtonFile ? '#A7E4C7' : '#24bb72'} />
          {t('requestVacancies.colaborativeSelection.attachedFile')}
        </HrCustomButton>

        <HrParagraph color='#767676'>
          <span style={{ fontWeight: '600', color: '#767676' }}>Tamaño máx.: </span>15MB{' '}
          <span style={{ fontWeight: '600', color: '#767676' }}>Formato: </span>.pdf, .doc, .docx, .csv o .xls{' '}
          <span style={{ fontWeight: '600', color: '#767676' }}>Cantidad: </span> límite 3 archivos
        </HrParagraph>
      </div>
      {disabledButtonFile && <ReactHintooltip events attribute='data-rh' className='reactHint' />}
      <InputFile
        inputRef={inputRef}
        inputFile={fileSelected}
        formState={formState}
        control={control}
        setError={setError}
        setInputFile={setFileSelected}
        clearErrors={clearErrors}
        register={register}
        setValue={setValue}
        name='cv_attachedFiles'
        validate={{
          required: false,
          size: true,
          format: true
        }}
        isMultiple
        trigger={trigger}
        sizeFiles={sizeFiles}
        accept={accept}
      />
      {renderFilesSelected()}
      {fieldError?.message && <ErrorText color={HrRed}>{fieldError.message}</ErrorText>}
      {toastState.visible && (
        <Toast
          text={toastState.message}
          isVisible={toastState.visible}
          type={toastState.type}
          onClose={() => setToastState({ ...toastState, visible: false })}
          msDelay={6000}
        />
      )}
      {errors?.multipleFiles?.type === 'required' && (
        <>
          <ErrorMessage
            errors={errors}
            name='attachedFiles'
            render={({ message }) => <ErrorText color={HrRed}>{message}</ErrorText>}
          />
        </>
      )}
    </HrFlexContainer>
  );
};

MultipleFile.propTypes = {
  limitFiles: PropTypes.number,
  sizeFiles: PropTypes.number,
  required: PropTypes.bool,
  fieldError: PropTypes.shape({
    message: PropTypes.string
  }),
  clearFieldError: PropTypes.func,
  existingFiles: PropTypes.arrayOf(PropTypes.object),
  onOpenExistingFile: PropTypes.func
};

MultipleFile.defaultProps = {
  limitFiles: 1,
  sizeFiles: 15000000,
  existingFiles: [],
  onOpenExistingFile: () => {}
};
